import { z } from 'zod'
import { BILLING, MICROS_PER_CENT, MICROS_PER_DOLLAR } from '../constants/billing.js'
import type { TimeZone } from '../validators/tenant.validator.js'

/**
 * An integer amount that survives JSON. `Number()` rounds past
 * `Number.MAX_SAFE_INTEGER` (2^53 − 1, about $9 billion of micro-dollars).
 */
export const jsonAmountSchema = z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER)

export const nonnegativeJsonAmountSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)

export const positiveJsonAmountSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER)

/** A bigint as a JSON number. Throws where `Number()` would silently round. */
export function jsonAmount(value: bigint): number {
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new Error(`Amount out of range: ${value.toString()}`)
  }
  return Number(value)
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

export function centsToMicros(cents: number): number {
  return cents * MICROS_PER_CENT
}

/** Whole cents in an amount of micros, rounded down: refunds and display of what's refundable use whole cents. */
export function microsToWholeCents(micros: number): number {
  return Math.floor(micros / MICROS_PER_CENT)
}

/** "$19.95", rounded half away from zero to cents. A negative amount reads "−$0.05". */
export function formatMicros(micros: number): string {
  const cents = Math.sign(micros) * Math.round(Math.abs(micros) / MICROS_PER_CENT)
  return formatCents(cents)
}

/** A price per live envelope, which can be a fraction of a cent: "$0.05", "$0.0525". */
export function formatPrice(micros: number): string {
  if (micros % MICROS_PER_CENT === 0) return formatMicros(micros)
  return `$${(micros / MICROS_PER_DOLLAR).toFixed(6).replace(/0+$/, '')}`
}

export function formatCents(cents: number): string {
  const text = usd.format(Math.abs(cents) / 100)
  return cents < 0 ? `−${text}` : text
}

const DOLLARS = /^\$?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?$/

/** "20", "20.5", "20.50", "$1,000.00" → 2000, 2050, 2050, 100000. Anything else → null. */
export function parseDollarsToCents(text: string): number | null {
  const match = DOLLARS.exec(text.trim())
  if (!match?.[1]) return null
  const dollars = Number(match[1].replaceAll(',', ''))
  const cents = Number((match[2] ?? '').padEnd(2, '0'))
  const total = dollars * 100 + cents
  return Number.isSafeInteger(total) ? total : null
}

const PRICE = /^\$?(\d+)(?:\.(\d{1,6}))?$/

/** A price per live envelope typed in dollars, to the micro-dollar: "0.06", "$0.0525" → 60000, 52500. Anything else → null. */
export function parsePriceToMicros(text: string): number | null {
  const match = PRICE.exec(text.trim())
  if (!match?.[1]) return null
  const micros = Number(match[1]) * MICROS_PER_DOLLAR + Number((match[2] ?? '').padEnd(6, '0'))
  return Number.isSafeInteger(micros) ? micros : null
}

export interface RefundQuote {
  amountCents: number
  feeCents: number
  netCents: number
}

/** What a refund of unused paid credit returns: the amount less the fee (5%, rounded half up). $10.00 → fee $0.50, net $9.50. */
export function refundQuote(amountCents: number): RefundQuote {
  const feeCents = Math.round((amountCents * BILLING.refundFeePercent) / 100)
  return { amountCents, feeCents, netCents: amountCents - feeCents }
}

/** Calendar month in a named timezone, `YYYY-MM` (the auto-reload cap's month). */
export function monthStampInZone(at: Date, timeZone: TimeZone): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit' }).format(at)
}
